/**
 * useAICoachPageState Hook
 *
 * Manages all state and logic for the AI Coach page.
 * Integrates with:
 * - useAICoachSession: Query hook for session data with polling
 * - useTriggerAICoach: Mutation to trigger AI Coach for a part
 * - useWherebyRoom: Hook for video call room info
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '../../../../../apiLayer/queryKeys'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { useAICoachSession } from '../../../../../apiLayer/queryHooks/queries'
import { useWherebyRoom } from '../../../../../shared/hooks/useWherebyRoom'
import { useUser } from '../../../../../hooks/useUser'
import useAppState from '../../../../../hooks/useAppState'
import { useTriggerAICoach, useReplayAICoach } from '../../../../../apiLayer/queryHooks/mutations'
import {
  getNormalizedCompletedParts,
  canReplayAICoachSession,
  isAlreadySpeakingRefusal
} from '../../../helpers/aiCoachHelpers'
import {
  AI_COACH_INTRO_PART,
  AI_COACH_LAST_PART,
  AI_COACH_PARTS,
  type AICoachPart,
  type AICoachSessionData
} from '../../../../../../../pure-js/types/AICoach'
import { getWherebyCallDisplayName } from '../../../../../../../pure-js/libs/isWherebyBotParticipant'

/** Polling interval for session data refresh (ms) */
const SESSION_POLLING_INTERVAL = 5000

export interface AICoachPageState {
  // Session state
  sessionData: AICoachSessionData | undefined
  effectiveSessionData: AICoachSessionData | undefined
  isLoading: boolean
  hasError: Error | null | unknown
  hasNoSession: boolean
  enabledDevAICoach: boolean

  // Participants
  participants: string[]
  participantNames: Record<string, string>
  currentUserId: string | undefined

  // Progress state
  currentPart: AICoachPart | null

  // AI state
  isAISpeaking: boolean
  isAssistantInCall: boolean
  setIsAssistantInCall: (inCall: boolean) => void
  isContentReady: boolean // Whether AI content + TTS audio is ready for current part
  /** The coach is genuinely still generating this part — not merely 'not ready'. */
  isPreparingContent: boolean

  // Action state
  isStarting: boolean
  isButtonLoading: boolean

  // Whereby
  roomUrl: string | undefined
  isWherebyRoomReady: boolean
  displayName: string
  teamName: string

  // Call state
  hasUserLeftCall: boolean
  setHasUserLeftCall: (left: boolean) => void

  // Replay state
  canReplay: boolean
  isReplaying: boolean

  // Actions
  handleContinue: () => Promise<void>
  handleReplay: () => Promise<void>
  handleLeaveToDashboard: () => void
}

export function useAICoachPageState(options?: { sessionId?: string }): AICoachPageState {
  const { sessionId } = options || {}
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { state } = useAppState()
  const currentUserId = state.user?.id
  const [isAssistantInCall, setIsAssistantInCall] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [hasUserLeftCall, setHasUserLeftCall] = useState(false)

  // Check for enabledDevAICoach in URL parameters
  const searchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  const enabledDevAICoach = searchParams.get('enabledDevAICoach') === 'true'

  // Get session data with auto-join and polling
  const {
    data: sessionResponse,
    isLoading: isSessionLoading,
    error: sessionError
  } = useAICoachSession({
    pollingInterval: SESSION_POLLING_INTERVAL,
    sessionId
  })

  const sessionData = sessionResponse?.data
  const teamIdForWhereby = sessionData?.teamId

  // Get Whereby room info
  const { meetingInfo, isLoading: isWherebyLoading, error: wherebyError } = useWherebyRoom({ teamId: teamIdForWhereby })

  // Get current user data
  const { data: currentUser } = useUser(currentUserId || '')

  const isLoading = isSessionLoading || isWherebyLoading
  // A backend 'error' status means pregeneration failed — surface the error screen instead of an
  // endless "Analysing…" spinner (which is what 'pregenerating' renders).
  const hasError = sessionError || wherebyError || sessionData?.status === 'error'
  const hasNoSession = !isLoading && !sessionData

  // Create mock session data for dev mode
  const mockSessionData: AICoachSessionData | undefined = useMemo(() => {
    if (!enabledDevAICoach || !hasNoSession) return undefined
    const userTeamIds = state.user?.teamIds || []
    const mockTeamId = userTeamIds[0] || 'dev-team-id'
    return {
      sessionId: 'dev-session-id',
      activityId: 'dev-activity-id',
      sprintId: 'dev-sprint-id',
      teamId: mockTeamId,
      status: 'available',
      currentPart: null,
      completedParts: [],
      participants: currentUserId ? [currentUserId, 'user-2', 'user-3', 'user-4'] : []
    }
  }, [enabledDevAICoach, hasNoSession, state.user?.teamIds, currentUserId])

  const effectiveSessionData = enabledDevAICoach && hasNoSession ? mockSessionData : sessionData

  // Map participant IDs to display names: the session lists them, the player's own name is the one they hold now
  const sessionParticipantNames = effectiveSessionData?.participantNames
  const participantNames = useMemo(() => {
    const nameMap: Record<string, string> = { ...sessionParticipantNames }
    if (currentUserId) {
      const currentUserResolvedName = currentUser?.displayName || currentUser?.email?.split('@')[0]
      if (currentUserResolvedName) nameMap[currentUserId] = currentUserResolvedName
    }
    // Mock names for dev mode
    if (enabledDevAICoach) {
      nameMap['user-2'] = 'Alice'
      nameMap['user-3'] = 'Bob'
      nameMap['user-4'] = 'Charlie'
    }
    return nameMap
  }, [sessionParticipantNames, currentUser?.displayName, currentUser?.email, currentUserId, enabledDevAICoach])

  // Determine current part based on completedParts (clearer logic)
  const currentPart = useMemo(() => {
    if (!effectiveSessionData) return null
    if (effectiveSessionData.status === 'completed') return null

    // Use completedParts if available (new approach)
    const completedParts = getNormalizedCompletedParts(effectiveSessionData)
    if (completedParts.length === AI_COACH_PARTS.length) return null // All parts done

    // If AI is speaking, currentPart shows what's being played
    if (effectiveSessionData.status === 'ai_speaking') {
      return effectiveSessionData.currentPart
    }

    if (completedParts.length > 0) {
      const nextPart = AI_COACH_PARTS.find((part) => !completedParts.includes(part))
      if (nextPart !== undefined) return nextPart
    } else if (effectiveSessionData.currentPart === null || effectiveSessionData.currentPart === undefined) {
      return AI_COACH_INTRO_PART
    }

    // Fallback to old logic if completedParts is empty but we have currentPart
    const basePart = effectiveSessionData.currentPart ?? AI_COACH_INTRO_PART
    const assistantCompletedBasePart =
      effectiveSessionData.assistantTask?.status === 'completed' &&
      effectiveSessionData.assistantTask?.part === basePart
    if (effectiveSessionData.status === 'paused' || assistantCompletedBasePart) {
      if (basePart === AI_COACH_LAST_PART) return null
      return (basePart + 1) as AICoachPart
    }
    return basePart as AICoachPart
  }, [effectiveSessionData])

  // Check if AI is currently speaking
  const isAISpeaking = useMemo(() => {
    return effectiveSessionData?.assistantTask?.status === 'running' || effectiveSessionData?.status === 'ai_speaking'
  }, [effectiveSessionData])

  // Check if AI content and TTS audio are ready for the current part
  const isContentReady = useMemo(() => {
    if (!effectiveSessionData || currentPart === null) return false
    // In dev mode, always consider content ready
    if (enabledDevAICoach) return true

    const aiContent = effectiveSessionData.aiContent
    const ttsAudio = effectiveSessionData.ttsAudio

    // Check if content exists for the current part
    const segmentKey = currentPart === AI_COACH_INTRO_PART ? 'intro' : (`segment${currentPart}` as const)
    const audioUrlKey = currentPart === AI_COACH_INTRO_PART ? 'introUrl' : (`segment${currentPart}Url` as const)

    const hasContent = !!aiContent?.[segmentKey]
    const hasAudio = !!ttsAudio?.[audioUrlKey]

    // Primary check: both content AND audio ready
    if (hasContent && hasAudio) return true

    // Fallback: session is no longer pregenerating AND content exists.
    // This prevents getting stuck "Analysing..." when TTS audio URL propagation
    // lags behind content generation — the session status transitioning away from
    // 'pregenerating' is the authoritative signal that the backend is done.
    if (effectiveSessionData.status !== 'pregenerating' && hasContent) return true

    return false
  }, [effectiveSessionData, currentPart, enabledDevAICoach])

  // "Not ready" and "still working" are different things: only a session that is actually
  // pregenerating is preparing, so a failed one does not sit on a spinner for ever.
  const isPreparingContent = useMemo(() => {
    if (isContentReady || currentPart === null) return false
    return effectiveSessionData?.status === 'pregenerating'
  }, [effectiveSessionData?.status, isContentReady, currentPart])

  const showActionError = useCallback(
    (error: Error, messageKey: string) => {
      // Someone else in the call pressed first: show what they started, not an error.
      if (isAlreadySpeakingRefusal(error)) {
        void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AI_COACH_SESSION] })
        return
      }

      toast.error(t(messageKey, { error: error.message }))
    },
    [queryClient, t]
  )

  // Mutations
  const triggerMutation = useTriggerAICoach({
    onSuccess: () => {
      setIsStarting(false)
      toast.success(t('aiCoach.actions.startSuccess'))
    },
    onError: (error) => {
      setIsStarting(false)
      showActionError(error, 'aiCoach.actions.startError')
    }
  })

  const replayMutation = useReplayAICoach({
    onSuccess: () => {
      toast.success(t('aiCoach.actions.replaySuccess'))
    },
    onError: (error) => showActionError(error, 'aiCoach.actions.replayError')
  })

  const participants = effectiveSessionData?.participants || []

  /**
   * Handle continue button click — trigger the next part directly.
   */
  const handleContinue = useCallback(async () => {
    if (!effectiveSessionData || currentPart === null || isStarting || isAISpeaking) return

    setIsStarting(true)
    try {
      await triggerMutation.mutateAsync({
        sessionId: effectiveSessionData.sessionId,
        part: currentPart
      })
    } catch {
      // Error handled
    }
  }, [effectiveSessionData, currentPart, isStarting, isAISpeaking, triggerMutation])

  // Replay re-speaks the current step for everyone (reuses cached audio).
  const isReplaying = replayMutation.isPending

  const canReplay = useMemo(() => {
    if (enabledDevAICoach) return !!effectiveSessionData && !isAISpeaking
    return canReplayAICoachSession(effectiveSessionData) && !isAISpeaking
  }, [effectiveSessionData, enabledDevAICoach, isAISpeaking])

  const handleReplay = useCallback(async () => {
    if (!effectiveSessionData || !canReplay) return
    if (enabledDevAICoach && hasNoSession) return
    try {
      await replayMutation.mutateAsync({ sessionId: effectiveSessionData.sessionId })
    } catch {
      // Error handled by onError callback
    }
  }, [effectiveSessionData, canReplay, enabledDevAICoach, hasNoSession, replayMutation])

  // Whereby info
  // Prefer the room URL stored on the AI Coach session so all participants and
  // the AI assistant use the same room. Fall back only for legacy sessions that
  // predate the stored wherebyRoomUrl field.
  const sessionWherebyRoomUrl = effectiveSessionData?.wherebyRoomUrl
  const legacyRoomFallbackAllowed = Boolean(
    effectiveSessionData &&
      !effectiveSessionData.wherebyRoomUrl &&
      (effectiveSessionData.currentPart !== null ||
        effectiveSessionData.completedParts.length > 0 ||
        effectiveSessionData.status === 'paused' ||
        effectiveSessionData.status === 'completed')
  )
  const roomUrl = sessionWherebyRoomUrl || (legacyRoomFallbackAllowed ? meetingInfo?.roomUrl : undefined)
  const isWherebyRoomReady = Boolean(roomUrl)

  // The signed-in user stands in when the user read came back empty.
  const displayName =
    meetingInfo?.displayName || getWherebyCallDisplayName(currentUser) || getWherebyCallDisplayName(state.user)

  const teamName = meetingInfo?.teamName || 'Team'

  /**
   * Navigate to dashboard when leaving the call or when session is complete
   */
  const handleLeaveToDashboard = useCallback(() => {
    setHasUserLeftCall(true)
    navigate('/gamer')
  }, [navigate])

  // A facilitator or operator ended the debrief for the team: leave once, to a dashboard that re-resolves the
  // next activity. A facilitator watching by session id stays — it is not their debrief.
  const forceEndedAt = sessionData?.forceEndedAt
  const hasLeftForForcedEndRef = useRef(false)
  useEffect(() => {
    if (!forceEndedAt || sessionId || hasLeftForForcedEndRef.current) return
    hasLeftForForcedEndRef.current = true
    void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER] })
    handleLeaveToDashboard()
  }, [forceEndedAt, sessionId, queryClient, handleLeaveToDashboard])

  return {
    sessionData,
    effectiveSessionData,
    isLoading,
    hasError,
    hasNoSession,
    enabledDevAICoach,
    participants,
    participantNames,
    currentUserId,
    currentPart,
    isAISpeaking,
    isAssistantInCall,
    setIsAssistantInCall,
    isContentReady,
    isPreparingContent,
    isStarting,
    isButtonLoading: isStarting || triggerMutation.isPending,
    roomUrl,
    isWherebyRoomReady,
    displayName,
    teamName,
    hasUserLeftCall,
    setHasUserLeftCall,
    canReplay,
    isReplaying,
    handleContinue,
    handleReplay,
    handleLeaveToDashboard
  }
}
